import { Api, getRankIconInfo } from './';
import { AsyncDepositBonusOffers, AsyncFreebets, AsyncInfoPlayer, AsyncInfoSelf, AsyncQuests, AsyncRaces, AsyncVipLevels, AsyncVipLevelsTranslated, AsyncVipState, ClaimReward, EntityType, RewardType } from './api/api';
import { isSuccessStatus } from './types';
import { getThemeSetting, stateVipLevelsTranslationEnabled } from '@infinitro/config';
import { derived, get, writable } from 'svelte/store';
import { loggedIn } from './auth';
import { momentjs as moment } from './helpers/datetime';
import { deepClone } from './helpers/deepClone';
import { getPercentage } from './helpers/format';
import { locale } from './locale';
import { parseSublevelFromString } from './rank-helpers';
import { debounceAsync, onceInTimeAsyncWithCatch } from './utils';
export { EntityType, RewardType };
let _loggedIn = undefined;
const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export const isUUID = (id) => UUID_REGEX.test(id);
export const getUserRankNameFromString = (rank) => rank ? rank.split(' ')[0].toLowerCase() : undefined;
export const getUserRankFromString = (rank) => rank ? rank.split(' ')[0].toLowerCase() : undefined;
/** Level index as string for badge UI (`"1"`…`"11"`). Display names come from the API. */
export const getUserRankFromLevel = (n) => n >= 1 && n <= 11 ? String(n) : undefined;
/** Sublevel (0–5) parsed from backend rank string. Uses current theme's sublevel format. */
export const getUserRankLevelFromString = (rank) => {
    const n = parseSublevelFromString(rank);
    return n !== undefined ? n : undefined;
};
async function hardRefreshVipLevelsInternal() {
    return new Promise((resolve, reject) => {
        const request = get(stateVipLevelsTranslationEnabled)
            ? AsyncVipLevelsTranslated({
                variables: { language: get(locale).toUpperCase() },
                context: { api: Api.player }
            })
            : AsyncVipLevels({ variables: {}, context: { api: Api.player } });
        request
            .then((response) => {
            const vipLevels = response?.data?.vipLevels;
            stateVipLevels.set(vipLevels);
            resolve();
        })
            .catch((error) => {
            reject(error);
        });
    });
}
export const refreshVipLevels = onceInTimeAsyncWithCatch(hardRefreshVipLevelsInternal, 1000 * 60 * 60 * 24);
export const hardRefreshVipLevels = debounceAsync(hardRefreshVipLevelsInternal, 100);
export const stateVipLevels = writable([]);
export async function getVipState() {
    return get(stateVipState);
}
async function hardRefreshVipStateInternal() {
    console.log(`hardRefreshVipStateInternal: ${_loggedIn}`);
    if (_loggedIn !== true) {
        setTimeout(() => hardRefreshSelfInfoInternal(), 1000);
        return;
    }
    const language = get(locale).toUpperCase();
    console.log(`hardRefreshVipStateInternal: ${language}`);
    return new Promise((resolve, reject) => {
        AsyncVipState({
            fetchPolicy: 'network-only',
            variables: { language },
            context: { api: Api.player }
        })
            .then((response) => {
            const vipState = response?.data?.vipState;
            stateVipState.set(vipState ? deepClone(vipState) : vipState);
            if (vipState?.serverTime) {
                try {
                    const serverTime = moment().utc(vipState.serverTime).toDate();
                    const skew = Date.now() - serverTime.getTime();
                    if (skew > 200 || skew < -200)
                        console.log(`possible clock skew: ${skew} millseconds`);
                }
                catch {
                    // ignore
                }
            }
            resolve();
        })
            .catch((error) => {
            reject(error);
        });
    });
}
export const refreshVipState = onceInTimeAsyncWithCatch(hardRefreshVipStateInternal, 1000);
export const hardRefreshVipState = debounceAsync(hardRefreshVipStateInternal, 100);
export const stateVipState = writable(undefined);
export const stateFreebets = writable(undefined);
export const stateRaces = writable([]);
export const isRakeboostBoosted = writable(false);
export const isRakeboostFinished = writable(false);
export const rakeboostTimeRemaining = writable('00:00:00');
export const rakeboostProgress = writable(0);
export const rakeboostEndAt = derived([stateVipState], ([$stateVipState]) => {
    if ($stateVipState?.rakeboostEndAt &&
        moment().utc($stateVipState.rakeboostEndAt).toDate() > new Date()) {
        return moment().utc($stateVipState.rakeboostEndAt).toDate();
    }
    return undefined;
});
export const rakeboostRate = derived([stateVipState, rakeboostEndAt], ([$stateVipState, $rakeboostEndAt]) => {
    if ($rakeboostEndAt && new Date($rakeboostEndAt) > new Date()) {
        return $stateVipState?.rakeboostRate ?? 15;
    }
    else {
        return 0;
    }
});
export async function getSelfInfo() {
    return get(stateSelfInfo);
}
export async function hardRefreshSelfInfoInternal() {
    if (_loggedIn !== true) {
        setTimeout(() => hardRefreshSelfInfoInternal(), 1000);
        return;
    }
    return new Promise((resolve, reject) => {
        AsyncInfoSelf({
            fetchPolicy: 'network-only',
            variables: {},
            context: { api: Api.player }
        })
            .then((response) => {
            const infoSelf = response?.data?.infoSelf;
            stateSelfInfo.set(infoSelf);
            resolve();
        })
            .catch((error) => {
            reject(error);
        });
    });
}
export const refreshSelfInfo = onceInTimeAsyncWithCatch(hardRefreshSelfInfoInternal, 1000);
export const hardRefreshSelfInfo = debounceAsync(hardRefreshSelfInfoInternal, 100);
export const stateSelfInfo = writable(undefined);
export async function getDepositBonusOffersState() {
    return get(stateDepositBonusOffers);
}
async function hardRefreshDepositBonusOffersInternal() {
    if (_loggedIn !== true)
        return;
    return new Promise((resolve, reject) => {
        AsyncDepositBonusOffers({
            fetchPolicy: 'network-only',
            variables: {},
            context: { api: Api.player }
        })
            .then((response) => {
            const depositBonusOffers = response?.data?.depositBonusOffers;
            stateDepositBonusOffers.set(depositBonusOffers);
            resolve();
        })
            .catch((error) => {
            reject(error);
        });
    });
}
export async function hardRefreshRacesInternal() {
    if (getThemeSetting().racesEnabled !== true) {
        stateRaces.set([]);
        return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
        AsyncRaces({
            fetchPolicy: 'network-only',
            variables: {}
        })
            .then((response) => {
            const races = response?.data?.races?.map((race) => ({
                ...race,
                startDate: moment().utc(String(race.startDate)).toDate(),
                endDate: moment().utc(String(race.endDate)).toDate()
            })) ?? [];
            stateRaces.set(races);
            resolve();
        })
            .catch((error) => {
            reject(error);
        });
    });
}
export const refreshRaces = onceInTimeAsyncWithCatch(hardRefreshRacesInternal, 1000);
export const hardRefreshRaces = debounceAsync(hardRefreshRacesInternal, 100);
export async function refreshFreebets() {
    if (_loggedIn !== true)
        return;
    return new Promise((resolve, reject) => {
        AsyncFreebets({
            fetchPolicy: 'network-only',
            variables: {},
            context: { api: Api.player }
        })
            .then((response) => {
            const freebets = response?.data?.freebets;
            stateFreebets.set(freebets);
            resolve();
        })
            .catch((error) => {
            reject(error);
        });
    });
}
export async function hardRefreshQuestsInternal() {
    if (_loggedIn !== true)
        return;
    return new Promise((resolve, reject) => {
        AsyncQuests({
            fetchPolicy: 'network-only',
            variables: {},
            context: { api: Api.player }
        })
            .then((response) => {
            const quests = response?.data?.quest;
            stateQuests.set(quests);
            resolve();
        })
            .catch((error) => {
            reject(error);
        });
    });
}
export const refreshQuests = onceInTimeAsyncWithCatch(hardRefreshQuestsInternal, 1000);
export const hardRefreshQuests = debounceAsync(hardRefreshQuestsInternal, 100);
export const stateQuests = writable([]);
export const refreshDepositBonusOffers = onceInTimeAsyncWithCatch(hardRefreshDepositBonusOffersInternal, 1000);
export const hardRefreshDepositBonusOffers = debounceAsync(hardRefreshDepositBonusOffersInternal, 100);
export const stateDepositBonusOffers = writable(undefined);
export async function getUserInfo(username, playerId) {
    if (_loggedIn !== true)
        return;
    if (!username && !playerId) {
        const info = get(stateSelfInfo);
        if (info) {
            refreshSelfInfo();
            return info;
        }
        await refreshSelfInfo();
        return get(stateSelfInfo);
    }
    try {
        if (username && isUUID(username)) {
            playerId = username;
            username = undefined;
        }
        if (playerId && !isUUID(playerId) && !username) {
            console.log(`Invalid playerId ${playerId}`);
            return undefined;
        }
        const variables = { otherPlayerSearch: username, otherPlayerId: playerId };
        const response = await AsyncInfoPlayer({
            fetchPolicy: 'network-only',
            variables,
            context: { api: Api.player }
        });
        return response?.data?.infoPlayer;
    }
    catch (error) {
        console.log(`Error fetching user ${username}`, error);
    }
}
export const stateCurrentPoints = derived([stateSelfInfo], ([$stateSelfInfo]) => $stateSelfInfo?.vipPoints ?? 0);
export const stateNextPoints = derived([stateSelfInfo], ([$stateSelfInfo]) => $stateSelfInfo?.vipPointsNext ?? 0);
// export const stateNext3Ranks = derived([stateSelfInfo, stateVipLevels], ([$stateSelfInfo, $stateVipLevels]) => {
// 	const nextRanks = $stateSelfInfo?.nextRanks;
// 	if (!nextRanks) return [];
// 	return nextRanks.slice(0, 3);
// });
export const stateVipProgressNextRank = derived([stateVipState], ([$stateVipState]) => getPercentage(($stateVipState?.vipPoints ?? 0) - ($stateVipState?.vipPointsStart ?? 0), ($stateVipState?.vipPointsNextRank ?? 0) - ($stateVipState?.vipPointsStart ?? 0)));
/** Full rank label from API (`vipRank` on PlayerInfo / VipState), ready for display. */
export const stateUserVipRankDisplay = derived([stateSelfInfo, stateVipState], ([$si, $vs]) => $si?.vipRank?.trim() || $vs?.vipRank?.trim() || '');
/** Next rank label from API (`nextRank` on PlayerInfo / VipState), ready for display. */
export const stateUserNextRankDisplay = derived([stateSelfInfo, stateVipState], ([$si, $vs]) => $si?.nextRank?.trim() || $vs?.nextRank?.trim() || '');
export const toRankIcon = (rank, level) => {
    const info = getRankIconInfo(rank, level);
    return info.imageSrc;
};
// several VIP stats can update on either stateVipState or stateSelfInfo
export const stateVipProgress = writable(0);
export const stateVipPoints = writable(undefined);
export const stateVipPointsStart = writable(undefined);
export const stateVipPointsNextLevel = writable(undefined);
export const stateVipPointsNextRank = writable(undefined);
export const stateUserLevelNum = writable(undefined);
export const stateUserSublevelNum = writable(undefined);
export const stateUserNextLevelNum = writable(undefined);
export const stateUserNextLevelIcon = writable(undefined);
export const stateUserNextSublevelNum = writable(undefined);
export const stateUserRank = writable(undefined);
export const stateUserNextRank = writable(undefined);
export const stateUserRankIcon = writable(undefined);
export const stateUserLevel = writable(undefined);
export const statePointsToNextLevel = writable(undefined);
export const stateTotalRewards = writable(undefined);
export const stateChatUserInfoMap = writable(new Map());
export const stateNextLevelUpCashback = writable(undefined);
stateSelfInfo.subscribe((v) => {
    const rank = v?.levelNum != undefined ? getUserRankFromLevel(v?.levelNum) : undefined;
    const nextRank = v?.levelNum != undefined ? getUserRankFromLevel(v?.levelNum + 1) : undefined;
    stateUserRank.set(rank);
    stateUserNextRank.set(nextRank);
    stateTotalRewards.set(v?.totalRewards);
    stateUserLevelNum.set(v?.levelNum ?? undefined);
    stateUserSublevelNum.set(v?.sublevelNum ?? undefined);
    stateUserNextLevelNum.set(v?.nextLevelNum ?? undefined);
    stateUserNextSublevelNum.set(v?.nextSublevelNum ?? undefined);
    stateVipPoints.set(v?.vipPoints ?? undefined);
    stateVipPointsStart.set(v?.vipPointsStart ?? undefined);
    stateVipPointsNextLevel.set(v?.vipPointsNext ?? undefined);
    stateUserLevel.set(v?.sublevelNum);
    statePointsToNextLevel.set(v?.vipPointsNext != null && v?.vipPoints != null ? v?.vipPointsNext - v?.vipPoints : undefined);
    stateVipProgress.set(getPercentage((v?.vipPoints ?? 0) - (v?.vipPointsStart ?? 0), (v?.vipPointsNext ?? 0) - (v?.vipPointsStart ?? 0)));
});
stateVipState.subscribe((v) => {
    const rank = v?.levelNum != undefined ? getUserRankFromLevel(v?.levelNum) : undefined;
    const nextRank = v?.nextLevelNum != undefined ? getUserRankFromLevel(v?.nextLevelNum) : undefined;
    stateUserRank.set(rank);
    stateUserNextRank.set(nextRank);
    stateTotalRewards.set(v?.totalRewarded);
    stateUserLevelNum.set(v?.levelNum ?? undefined);
    stateUserSublevelNum.set(v?.sublevelNum ?? undefined);
    stateUserNextLevelNum.set(v?.nextLevelNum ?? undefined);
    stateUserNextSublevelNum.set(v?.nextSublevelNum ?? undefined);
    stateVipPoints.set(v?.vipPoints ?? undefined);
    stateVipPointsStart.set(v?.vipPointsStart ?? undefined);
    stateVipPointsNextLevel.set(v?.vipPointsNextLevel ?? undefined);
    stateVipPointsNextRank.set(v?.vipPointsNextRank ?? undefined);
    statePointsToNextLevel.set(v?.vipPointsNextLevel != null && v?.vipPoints != null
        ? v?.vipPointsNextLevel - v?.vipPoints
        : undefined);
    stateVipProgress.set(getPercentage((v?.vipPoints ?? 0) - (v?.vipPointsStart ?? 0), (v?.vipPointsNextLevel ?? 0) - (v?.vipPointsStart ?? 0)));
    stateNextLevelUpCashback.set(v?.nextLevelUpCashback ?? undefined);
});
export async function getFreebets() {
    if (_loggedIn !== true)
        return;
    const vars = { variables: {}, context: { api: Api.player } };
    const result = await AsyncFreebets(vars);
    return result.data?.freebets;
}
export async function getQuests() {
    if (_loggedIn !== true)
        return;
    const vars = { variables: {}, context: { api: Api.player } };
    const result = await AsyncQuests(vars);
    return result.data?.quest;
}
export async function getRaces() {
    if (getThemeSetting().racesEnabled !== true) {
        return [];
    }
    const result = await AsyncRaces({});
    return (result.data?.races?.map((race) => ({
        ...race,
        startDate: moment().utc(String(race.startDate)).toDate(),
        endDate: moment().utc(String(race.endDate)).toDate()
    })) ?? []);
}
export async function rewardClaim(rewardType, currency, accountNum, claimId) {
    if (_loggedIn !== true)
        return;
    if (!rewardType)
        return;
    if (!accountNum)
        return;
    if (!currency)
        return;
    const vars = {
        variables: { rewardType, currency, accountNum, claimId },
        context: { api: Api.player }
    };
    const result = await ClaimReward(vars);
    hardRefreshVipState();
    return result.data?.claimReward;
}
/**
 * Instant Cashback claim/reset via the shared `claimReward` mutation.
 * Maps to `CASHBACK_INSTANT` / `RESET_CASHBACK_INSTANT` and classifies
 * `reqType` ineligibility (BALANCE / GAME / CASHBACK / WITHDRAWAL) on claim, or on
 * reset when status is not successful. Dry-run phase:
 * backend reports eligibility / would-be amount without awarding or resetting.
 * Never throws: a failed mutation resolves with `error` set so callers can
 * always give the player feedback.
 */
export async function instantCashbackClaim(currency, accountNum, kind = 'claim') {
    const rewardType = kind === 'reset' ? RewardType.RESET_CASHBACK_INSTANT : RewardType.CASHBACK_INSTANT;
    let result;
    try {
        result = await rewardClaim(rewardType, currency, accountNum);
    }
    catch (error) {
        console.warn(`instantCashbackClaim (${kind}) failed:`, error);
        return { isSuccess: false, error };
    }
    const reqType = result?.reqType ?? undefined;
    const reqTxtValues = result?.reqTxtValues ?? undefined;
    const statusOk = Boolean(result?.status && isSuccessStatus(result.status));
    const isIneligibleBlocker = reqType === EntityType.BALANCE ||
        reqType === EntityType.GAME ||
        reqType === EntityType.CASHBACK ||
        reqType === EntityType.WITHDRAWAL;
    // Claim: any blocker reqType means ineligible. Reset: blockers apply only on failure —
    // a successful reset can still echo reqType CASHBACK from the API.
    const isIneligible = kind === 'reset'
        ? Boolean(!statusOk && isIneligibleBlocker)
        : Boolean(isIneligibleBlocker);
    // Claim preview needs a would-be amount; reset only needs a success status.
    const isSuccess = kind === 'reset'
        ? Boolean(statusOk && !isIneligible)
        : Boolean(!isIneligible && statusOk && result?.txAmountBase != null);
    return { result, isSuccess, reqType: isIneligible ? reqType : undefined, reqTxtValues };
}
loggedIn.subscribe((loggedIn) => {
    if (_loggedIn === loggedIn)
        return;
    if (loggedIn === false) {
        stateVipState.set(undefined);
        stateSelfInfo.set(undefined);
        _loggedIn = loggedIn;
    }
    else if (loggedIn === true) {
        // hardRefreshSelfInfo();
        _loggedIn = loggedIn;
    }
});
